import { DangerConfirmDialog } from "@/routes/admin/DangerConfirmDialog";
import {
  ERASURE_DESTRUCTION_SENTENCE,
  ERASURE_RETENTION_STATEMENTS,
} from "@/routes/admin/erasure-retention-copy";

// Story 11.12 AC10 (FR56d) — the Delete User confirmation.
//
// The UX spec mandates a modal here and records that the prototype skips one
// (`ux-design-specification.md:832`). This is the only irreversible control on
// Screen 21: Disable can be undone with Reactivate, a password can be reset
// again, and this cannot be undone at all.
//
// WHAT IS KEPT IS SAID BEFORE WHAT IS DESTROYED. An admin pressing Delete on a
// GDPR erasure request needs to know two different things, and only one of them
// is obvious: that the account really is gone, and that the clinical record and
// the audit trail deliberately are not. Presenting only the destruction invites
// the escalation "you said you deleted them, why is their name on this
// evaluation" — which is the retention the platform is required to honour
// (7 years, medicolegal), not a bug.
//
// THE SENTENCES ARE NOT WRITTEN HERE. They come from `erasure-retention-copy`,
// which the post-erase banner on Screen 20 renders from too. The two used to be
// hand-written separately and had already drifted — four statements here, two
// there — so the admin was told one story before pressing the button and a
// shorter one after. That file also explains why the copy is a constant rather
// than something built from the server's `layers_retained`: this modal opens
// before any request exists, so its text has to be static.
//
// The retention wording is the product manager's, and two of its choices are
// load-bearing rather than stylistic:
//
//  • The audit sentence says the actions are recorded "against an internal
//    identifier rather than a name" — it does NOT say the person is anonymous
//    or no longer identifiable, because they are not. Erasure writes an
//    attribution row mapping the user id to a display name, so the actor stays
//    re-identifiable through a deliberate, narrow, database-level path; the
//    GDPR word for that is *pseudonymised*. An earlier draft of this modal
//    claimed non-identifiability. Do not put it back.
//  • The clinical sentence gives the REASON the evaluations keep the name (an
//    AMPS score is only readable beside its rater's calibrated severity). An
//    admin who has to defend the retention needs the argument, not just the
//    fact.
//
// One thing the current wording does NOT say: audit rows written before the
// forward rule that keeps addresses out of payloads may still contain the
// person's e-mail or an administrator's free text, and the trail is append-only
// at the database level, so no later story can go back and fix them. That
// residue is documented where it is acted on rather than in this modal —
// `erase_user_account/endpoint.py` for the engineer, and step 11 of TC-E11-04
// for the tester, which classifies finding one as a disclosure escalation.
//
// The billing statement closes a gap by omission. The retentions above it name
// everything the platform itself holds and then stop, which reads as "and
// nothing else survives" — but the payment provider holds an identity of its
// own, and invoices fall under financial-record retention exactly as the trail
// falls under medicolegal retention. Same principle, different party. It
// therefore does NOT say the provider's copy disappears, because it will not
// and cannot. Nothing in this dialog may promise what erasure does not deliver.

const DELETE_USER_MUST_READ = (
  <ul className="list-disc pl-5">
    <li>{ERASURE_DESTRUCTION_SENTENCE}</li>
    {ERASURE_RETENTION_STATEMENTS.map((statement) => (
      <li key={statement.layer}>{statement.sentence}</li>
    ))}
  </ul>
);

export interface DeleteUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userName: string;
  isPending: boolean;
  onConfirm: () => void;
}

export function DeleteUserDialog({
  open,
  onOpenChange,
  userName,
  isPending,
  onConfirm,
}: DeleteUserDialogProps) {
  return (
    <DangerConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Delete this account?"
      description={`${userName}'s account will be permanently erased.`}
      mustRead={DELETE_USER_MUST_READ}
      mustReadVariant="danger"
      confirmVariant="destructive"
      confirmLabel="Delete user"
      pendingLabel="Deleting…"
      isPending={isPending}
      onConfirm={onConfirm}
    />
  );
}
